/**
 * MeetingDetailHeader — 会议详情头部：标题、关联事项、时间、参会人、来源、
 * 处理阶段标签，以及主操作（S11 确认门禁 T-260823-14）。
 *
 * 主操作由底层字段派生，不按 processingStage 分支——processingStage 是多个
 * 正交状态揉合出的总览标签，会在字段组合尚未反映到该标签前就先行变化（例如
 * 待办抽取失败让总览标签变成「失败」，但会后总结早已就绪，此时仍要能确认）：
 * - `confirmed`（`confirmedAt` 非空）：既有归档入口（MeetingArchiveSection）+
 *   未归档时给「撤销确认」；已归档（`minutesDocumentId` 非空）时归档入口自身
 *   切换成「查看纪要文档」，不再展示撤销确认。
 * - 未确认但 `summaryState === "ready"`：「确认纪要」。
 * - 未确认且总结未就绪：按 processingStage 给出准备中的提示文案；
 *   `pending_analysis`（转写已完成、分析尚未开始）额外给「生成会后总结」按钮。
 * - `processingStage === "failed"` 时的失败提示与上述主操作并列展示，不互斥
 *   ——失败可能只是待办抽取或会前准备这两项之一，会后总结仍可能已经就绪。
 *
 * 折叠了原「概览」页签的全部内容——处理阶段取代后不再单列概览页签。
 */

import { useState } from "react";
import { Alert, Button, Form, Input, Modal, Popconfirm, Select, Space, Tag, Tooltip, Typography, message } from "antd";
import {
  AudioOutlined,
  CheckCircleOutlined,
  CloudDownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { isApiError } from "../../api";
import {
  useArchiveMeeting,
  useConfirmMeeting,
  useDeleteMeeting,
  useRevokeMeetingConfirmation,
  useSubmitMeetingAiAction,
  useUpdateMeeting,
} from "../../api/queries";
import type { Meeting, MeetingProcessingStage, MeetingStatus } from "../../types/meeting";
import {
  MEETING_SOURCE_LABELS,
  MEETING_STATUS_LABELS,
  MEETING_STATUS_OPTIONS,
  TRANSCRIPT_STATUS_LABELS,
} from "../../types/meeting";
import MeetingArchiveSection from "./MeetingArchiveSection";
import MeetingMatterBinding from "./MeetingMatterBinding";
import MeetingProcessingStageTag from "./MeetingProcessingStageTag";

const { Text, Title } = Typography;

/** 元信息项：单行展示，超长尾部省略（地点链接、邮箱、参会人数都可能很长）。 */
const META_ITEM_STYLE = {
  minWidth: 40,
  maxWidth: 240,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

/**
 * 总结尚未就绪时，按处理阶段给出的准备中提示。只覆盖「转写/分析仍在进行」
 * 这类过程态；`pending_analysis` 单独处理（给按钮而不是纯文案），`failed`
 * 不在此列——失败原因已由上方的失败提示单独说明，这里不重复。
 */
const PREPARING_STAGE_HINT: Partial<Record<MeetingProcessingStage, string>> = {
  pending_upload: "这场会还没有可转写的内容。",
  transcribing: "录音正在转写，完成后可以生成纪要。",
  analyzing: "AI 正在生成纪要，请稍候。",
};

function formatTimeRange(startsAt: string | null, endsAt: string | null): string {
  if (!startsAt) return "时间未定";
  const start = new Date(startsAt);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  if (!endsAt) return fmt(start);
  const end = new Date(endsAt);
  return `${fmt(start)} — ${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`;
}

interface MeetingDetailHeaderProps {
  meeting: Meeting;
  onImport: () => void;
  onDeleted: () => void;
}

export default function MeetingDetailHeader({
  meeting,
  onImport,
  onDeleted,
}: MeetingDetailHeaderProps) {
  const [messageApi, contextHolder] = message.useMessage();
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const updateMut = useUpdateMeeting();
  const deleteMut = useDeleteMeeting();
  const archiveMut = useArchiveMeeting();
  const confirmMut = useConfirmMeeting();
  const revokeMut = useRevokeMeetingConfirmation();
  const submitActionMut = useSubmitMeetingAiAction();

  const imported = meeting.importState === "imported";
  const participants = meeting.participantEmails ?? [];
  // 主操作的判据全部来自底层字段，不读 processingStage——理由见文件头注释。
  const confirmed = !!meeting.confirmedAt;
  const archived = !!meeting.minutesDocumentId;
  const summaryReady = meeting.summaryState === "ready";

  const handleSaveEdit = async () => {
    let values: {
      title?: string;
      status?: MeetingStatus;
      organizerEmail?: string;
      locationOrUrl?: string;
    };
    try {
      values = await editForm.validateFields();
    } catch {
      return;
    }
    try {
      await updateMut.mutateAsync({ id: meeting.id, data: values });
      messageApi.success("已保存");
      setEditOpen(false);
    } catch {
      messageApi.error("保存失败");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMut.mutateAsync(meeting.id);
      messageApi.success("已删除");
      onDeleted();
    } catch {
      messageApi.error("删除失败");
    }
  };

  const handleArchive = async () => {
    setArchiveError(null);
    try {
      await archiveMut.mutateAsync(meeting.id);
      messageApi.success("纪要已归档，可在文档库中检索到");
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : "归档失败，请稍后重试。");
    }
  };

  // 409：确认门禁的前置条件在提交后才被服务端发现变化（如另一处已抢先确认），
  // catch 里给出后端消息即可——mutation 的 onSettled 已无条件刷新会议详情，
  // 界面状态会跟着服务端最新结果自动更正。
  const handleConfirm = async () => {
    try {
      await confirmMut.mutateAsync(meeting.id);
      messageApi.success("已确认纪要，待办确认与归档现已可用");
    } catch (err) {
      messageApi.error(
        isApiError(err) && err.status === 409
          ? err.message || "确认未生效，请刷新后重试"
          : err instanceof Error
            ? err.message
            : "确认失败",
      );
    }
  };

  const handleRevoke = async () => {
    try {
      await revokeMut.mutateAsync(meeting.id);
      messageApi.success("已撤销确认，纪要退回草稿状态");
    } catch (err) {
      messageApi.error(
        isApiError(err) && err.status === 409
          ? err.message || "撤销未生效，该会议可能已归档"
          : err instanceof Error
            ? err.message
            : "撤销失败",
      );
    }
  };

  // `pending_analysis`（转写已完成、分析尚未开始）的行动号召：提交后由「会议
  // 纪要」页签的状态轮询接手展示进度，这里只负责起个头。
  const handleGenerateSummary = async () => {
    try {
      await submitActionMut.mutateAsync({ id: meeting.id, action: "summarize" });
      messageApi.success("已开始生成会后总结，完成后本页自动更新");
    } catch (err) {
      messageApi.error(err instanceof Error ? err.message : "提交失败");
    }
  };

  return (
    <div data-testid="meeting-detail-header">
      {contextHolder}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "var(--jerry-sp-3)",
          marginBottom: "var(--jerry-sp-3)",
        }}
      >
        <Title level={4} style={{ margin: 0 }} data-testid="meeting-detail-title">
          {meeting.title}
        </Title>
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              editForm.setFieldsValue({
                title: meeting.title,
                status: meeting.status,
                organizerEmail: meeting.organizerEmail ?? undefined,
                locationOrUrl: meeting.locationOrUrl ?? undefined,
              });
              setEditOpen(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm title="确认删除该会议？" onConfirm={handleDelete}>
            <Button danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      </div>

      {/* 标签与元信息合并为一条横向铺开的行（2026-08-27 紧凑化，设计讨论底稿 R1）：
          标签在左，时间/地点/主持人/参会人在右用足头部宽度，取代原先四段
          纵向堆叠的 Paragraph；参会人名单收进 Tooltip，超长项尾部省略。 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "var(--jerry-sp-1) var(--jerry-sp-3)",
          marginBottom: "var(--jerry-sp-3)",
        }}
      >
        <Space size={4} wrap style={{ flexShrink: 0 }}>
          <MeetingProcessingStageTag stage={meeting.processingStage} />
          <Tag>{MEETING_SOURCE_LABELS[meeting.source]}</Tag>
          <Tag>{MEETING_STATUS_LABELS[meeting.status]}</Tag>
          {imported ? (
            <Tag>
              <AudioOutlined style={{ marginRight: 4 }} />
              {TRANSCRIPT_STATUS_LABELS[meeting.transcriptStatus]}
            </Tag>
          ) : (
            <Tag data-testid="meeting-detail-not-imported">未导入内容</Tag>
          )}
        </Space>
        <div
          style={{
            marginLeft: "auto",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: "var(--jerry-sp-2)",
            color: "var(--jerry-text-tertiary)",
            fontSize: "var(--jerry-font-size-sm)",
          }}
        >
          <span style={{ flexShrink: 0 }}>{formatTimeRange(meeting.startsAt, meeting.endsAt)}</span>
          {meeting.locationOrUrl && (
            <span style={META_ITEM_STYLE}>· {meeting.locationOrUrl}</span>
          )}
          {meeting.organizerEmail && (
            <span style={META_ITEM_STYLE}>· 主持人 {meeting.organizerEmail}</span>
          )}
          {participants.length > 0 && (
            <Tooltip
              title={participants.map((p) => p.name || p.email || "未知").join("、")}
            >
              <span style={META_ITEM_STYLE}>· {participants.length} 位参会人</span>
            </Tooltip>
          )}
        </div>
      </div>

      {!imported && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: "var(--jerry-sp-3)" }}
          data-testid="meeting-detail-import-notice"
          message="这场会只有清单信息——导入内容之后才有纪要、逐字稿和待办"
          action={
            <Button
              type="primary"
              size="small"
              icon={<CloudDownloadOutlined />}
              data-testid="meeting-detail-import-action"
              onClick={onImport}
            >
              导入内容
            </Button>
          }
        />
      )}

      <MeetingMatterBinding meeting={meeting} />

      {imported && (
        <div style={{ marginTop: "var(--jerry-sp-4)" }} data-testid="meeting-detail-primary-action">
          {/* 失败提示与下方的主操作并列展示，不互斥：失败可能只是待办抽取或
              会前准备这两项之一，会后总结仍可能已经就绪、可以确认。 */}
          {meeting.processingStage === "failed" && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: "var(--jerry-sp-3)" }}
              data-testid="meeting-detail-processing-failed"
              message="本次处理未完成"
              description="具体原因见下方各页签内的失败提示，可在对应页签重试。"
            />
          )}

          {confirmed ? (
            <>
              <Title level={5}>纪要归档</Title>
              <MeetingArchiveSection
                meeting={meeting}
                archiving={archiveMut.isPending}
                errorMessage={archiveError}
                onArchive={handleArchive}
                confirmed={confirmed}
              />
              {!archived && (
                <Popconfirm title="撤销确认后纪要退回草稿状态，需重新确认" onConfirm={handleRevoke}>
                  <Button
                    style={{ marginTop: "var(--jerry-sp-2)" }}
                    loading={revokeMut.isPending}
                    data-testid="meeting-revoke-confirmation-button"
                  >
                    撤销确认
                  </Button>
                </Popconfirm>
              )}
            </>
          ) : summaryReady ? (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={confirmMut.isPending}
              onClick={handleConfirm}
              data-testid="meeting-confirm-button"
            >
              确认纪要
            </Button>
          ) : meeting.processingStage === "pending_analysis" ? (
            <Space align="center" wrap>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                loading={submitActionMut.isPending}
                onClick={handleGenerateSummary}
                data-testid="meeting-generate-summary-button"
              >
                生成会后总结
              </Button>
              <Text type="secondary">转写已完成，点击生成纪要。</Text>
            </Space>
          ) : meeting.processingStage !== "failed" ? (
            <Text type="secondary" data-testid="meeting-detail-stage-hint">
              {PREPARING_STAGE_HINT[meeting.processingStage] ?? "这场会还没有可确认的纪要。"}
            </Text>
          ) : null}
        </div>
      )}

      <Modal
        title="编辑会议"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={handleSaveEdit}
        confirmLoading={updateMut.isPending}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="title" label="主题" rules={[{ required: true }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={MEETING_STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item name="organizerEmail" label="主持人邮箱">
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="locationOrUrl" label="地点 / 链接">
            <Input maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
